// Copyright (c) 2025 GeoVerses. All rights reserved.

import React, { useState, useEffect, useCallback } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SignIn from './SignIn';

const MyPoems = ({ user, onSignIn, onEmailSignIn, onEmailSignUp, onSignOut }) => {
  const [poems, setPoems] = useState([]);
  const [editingPoem, setEditingPoem] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editAuthor, setEditAuthor] = useState('');
  const [editBody, setEditBody] = useState('');
  const [deleteConfirmPoem, setDeleteConfirmPoem] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);

  const fetchMyPoems = useCallback(async () => {
    if (!user) return;
    try {
      const idToken = await user.getIdToken();
      const response = await fetch('/getMyPoems', {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (!response.ok) throw new Error('Failed to fetch poems');
      const data = await response.json();
      setPoems(data.poems);
    } catch (error) {
      console.log(error);
    }
  }, [user]);

  useEffect(() => {
    fetchMyPoems();
  }, [fetchMyPoems]);

  const handleEditOpen = (poem) => {
    setEditingPoem(poem);
    setEditTitle(poem.PoemTitle);
    setEditAuthor(poem.PoemAuthor);
    setEditBody(poem.PoemText);
  };

  const handleEditClose = () => {
    setEditingPoem(null);
    setEditTitle('');
    setEditAuthor('');
    setEditBody('');
  };

  const handleEditSave = async () => {
    if (!user || !editingPoem) return;
    try {
      const idToken = await user.getIdToken();
      const response = await fetch('/updatePoem', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          poemId: editingPoem.PoemID,
          title: editTitle,
          author: editAuthor,
          body: editBody
        })
      });
      if (!response.ok) throw new Error('Update failed');
      handleEditClose();
      fetchMyPoems();
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!user || !deleteConfirmPoem) return;
    try {
      const idToken = await user.getIdToken();
      const response = await fetch('/deletePoem', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ poemId: deleteConfirmPoem.PoemID })
      });
      if (!response.ok) throw new Error('Delete failed');
      setDeleteConfirmPoem(null);
      fetchMyPoems();
    } catch (error) {
      console.log(error);
    }
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
  };

  if (!user) {
    return (
      <div className="page-container">
        <Typography variant="h5" component="h1" sx={{ my: 2 }}>My Poems</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>Sign in to view your poems</Typography>
        <SignIn onGoogleSignIn={onSignIn} onEmailSignIn={onEmailSignIn} onEmailSignUp={onEmailSignUp} />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginTop: '8px' }}>
        <Typography variant="body2">Signed in as {user.displayName || user.email}</Typography>
        <Button size="small" onClick={onSignOut}>Sign out</Button>
      </div>
      <Typography variant="h5" component="h1" sx={{ my: 2 }}>My Poems</Typography>
      {poems.length === 0 ? (
        <Typography variant="body1">You haven't written any poems yet.</Typography>
      ) : (
        <div className="poem-reader-list">
          {poems.map((poem) => (
            <Accordion key={poem.PoemID} sx={{ width: '100%' }}>
              <AccordionSummary>
                <div className="poem-text" style={{ flex: 1 }}>
                  <strong>{poem.PoemTitle}</strong>
                  <br />
                  {poem.PoemAuthor}
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <p className="poem-text">{poem.PoemText}</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleEditOpen(poem); }}
                    aria-label="edit"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirmPoem(poem); }}
                    aria-label="delete"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={!!editingPoem}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') handleEditClose();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Poem</DialogTitle>
        <DialogContent>
          <div className="poem-text-box" style={{ marginTop: '8px' }}>
            <label>
              Title
              <textarea
                className="poem-entry-field"
                rows={1}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </label>
            <label>
              Author
              <textarea
                className="poem-entry-field"
                rows={1}
                value={editAuthor}
                onChange={(e) => setEditAuthor(e.target.value)}
              />
            </label>
            <label className="poem-body-field">
              Poem
              <textarea
                className="poem-entry-field"
                rows="10"
                wrap="off"
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
              />
            </label>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmPoem} onClose={() => setDeleteConfirmPoem(null)}>
        <DialogTitle>Delete Poem</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete "{deleteConfirmPoem?.PoemTitle}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmPoem(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={successOpen} onClose={handleSuccessClose}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography variant="body1">Poem submitted successfully!</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleSuccessClose}>OK</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MyPoems;
